<!-- src/components/settings/account/ChangePinDialog.svelte -->

<script>
  import { createEventDispatcher, tick } from 'svelte';
  import { Lock, Trash2, X } from 'lucide-svelte';
  import ApiClient from '$lib/api';
  import { confirm, loading, notify } from '@components/toast';
  import { minimumDelay } from '$lib/utils';

  const dispatch = createEventDispatcher();
  const api = new ApiClient();

  export let form = null;
  export let visible = false;
  export let pinLength = 4;
  export let hasPin = false;
  export let accountId = null;

  let pinInput;
  let wasVisible = false;

  let step = 1;

  let selectedPinLength = pinLength;

  let currentPin = '';
  let newPin = '';
  let confirmPin = '';

  let inputValue = '';

  let shake = false;
  let errorMessage = '';
  let verifying = false;

  let focusedIndex = -1;
  let deleting = false;

  const pinLengths = [2, 4, 6, 8];
  let selectedIndex = 0;
  let lengthButtons = [];

  $: hasExistingPin = !!(hasPin || form?.hasPin || form?.pinEnabled || form?.pinenabled);
  $: normalizedPinLength = normalizePinLength(pinLength);

  $: if (visible && !wasVisible) {
    reset(normalizedPinLength);
    wasVisible = true;
  }

  $: if (!visible && wasVisible) {
    wasVisible = false;
  }

  $: if (!visible || (visible && isPinLengthSyncedStep() && !inputValue)) {
    selectedPinLength = normalizedPinLength;
  }

  $: selectedIndex = Math.max(0, pinLengths.indexOf(selectedPinLength));

  $: focusedIndex = Math.min(inputValue.length, selectedPinLength - 1);

  $: displayStep = hasExistingPin ? step : Math.max(1, step - 1);
  $: totalSteps = hasExistingPin ? 4 : 3;

  $: currentTitle =
    step === 1
      ? 'Verify Current PIN'
      : step === 2
        ? 'Select PIN Length'
        : step === 3
          ? hasExistingPin
            ? 'Create New PIN'
            : 'Create PIN'
          : 'Confirm PIN';

  $: if (visible) {
    focusPin();
  }

  function normalizePinLength(value) {
    const nextPinLength = Number(value) || 4;

    return pinLengths.includes(nextPinLength) ? nextPinLength : 4;
  }

  function isPinLengthSyncedStep() {
    return hasExistingPin ? step === 1 : step === 2;
  }

  async function focusLengthButton(index) {
    await tick();

    lengthButtons[index]?.focus();
  }

  function choosePinLength(length) {
    if (!pinLengths.includes(length)) return;

    selectedPinLength = length;
  }

  function moveLeft() {
    if (selectedIndex <= 0) return;

    choosePinLength(pinLengths[selectedIndex - 1]);
    focusLengthButton(selectedIndex - 1);
  }

  function moveRight() {
    if (selectedIndex >= pinLengths.length - 1) return;

    choosePinLength(pinLengths[selectedIndex + 1]);
    focusLengthButton(selectedIndex + 1);
  }

  function handleLengthKeydown(event) {
    const numberKey = Number(event.key);

    if (pinLengths.includes(numberKey)) {
      event.preventDefault();
      choosePinLength(numberKey);
      focusLengthButton(pinLengths.indexOf(numberKey));
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      moveLeft();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      moveRight();
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      confirmPinLength();
    }
  }

  async function deletePin() {
    const userId = accountId || form?.id;

    if (!userId) {
      notify.error('User information is missing');
      return;
    }

    const ok = await confirm({
      type: 'delete',
      title: 'Xoá PIN',
      message: 'Bạn có muốn xoá PIN code hiện tại? Hành động này sẽ mở lại chế độ Create PIN.',
      confirmText: 'Xoá PIN',
      cancelText: 'Huỷ'
    });

    if (!ok) return;

    try {
      deleting = true;

      const rs = await loading.run(
        async () =>
          api.post('/api/delete-pin', {
            userId
          }),
        'Đang xoá PIN...'
      );

      if (!rs.success) {
        notify.error(rs.message || 'Delete PIN failed');
        return;
      }

      notify.success('PIN deleted');
      dispatch('deleted');
      close();
    } catch (err) {
      notify.error(err?.message || 'Delete PIN failed');
    } finally {
      deleting = false;
    }
  }

  function confirmPinLength() {
    step = 3;

    inputValue = '';
    errorMessage = '';

    focusPin();
  }

  async function focusPin() {
    await tick();

    setTimeout(() => {
      if (!visible || step === 2) return;

      pinInput?.focus();

      try {
        pinInput?.click();
      } catch {}
    }, 100);
  }

  function reset(nextPinLength = normalizedPinLength) {
    step = hasExistingPin ? 1 : 2;

    selectedPinLength = normalizePinLength(nextPinLength);

    currentPin = '';
    newPin = '';
    confirmPin = '';

    inputValue = '';

    errorMessage = '';
    shake = false;
    verifying = false;
  }

  function close() {
    reset();

    dispatch('close');
  }

  function clearPinInput() {
    inputValue = '';

    if (pinInput) {
      pinInput.value = '';
    }
  }

  function triggerShake(message) {
    errorMessage = message;

    shake = false;

    requestAnimationFrame(() => {
      shake = true;

      setTimeout(() => {
        shake = false;
        focusPin();
      }, 400);
    });
  }

  function selectPinLength(length) {
    choosePinLength(length);

    inputValue = '';

    errorMessage = '';

    step = 3;

    focusPin();
  }

  async function handleInput(event) {
    if (verifying) {
      event.target.value = inputValue;
      return;
    }

    let value = event.target.value || '';

    value = value.replace(/[^\d]/g, '');

    inputValue = value.slice(0, selectedPinLength);

    if (inputValue.length === selectedPinLength) {
      await tick();
      setTimeout(nextStep, 100);
    }
  }

  async function verifyCurrentPin() {
    if (!hasExistingPin) {
      step = 2;
      return;
    }

    const userId = accountId || form?.id;

    if (!userId) {
      clearPinInput();
      triggerShake('User information is missing');
      return;
    }

    const pin = inputValue;

    try {
      verifying = true;

      const rs = await loading.run(
        async () =>
          minimumDelay(
            api.post('/api/verify-pin', {
              userId,
              pin
            }),
            600
          ),
        'Đang xác thực PIN...'
      );

      if (rs.success) {
        currentPin = pin;

        clearPinInput();
        errorMessage = '';
        step = 2;

        await tick();
        lengthButtons[selectedIndex]?.focus();

        return;
      }

      clearPinInput();

      if (rs.locked) {
        triggerShake('PIN is temporarily locked');
        return;
      }

      triggerShake(rs.message || 'Current PIN is invalid');
    } catch (err) {
      console.error(err);

      clearPinInput();
      triggerShake(err?.message || 'Verify PIN failed');
    } finally {
      verifying = false;
    }
  }

  async function nextStep() {
    if (step === 1) {
      await verifyCurrentPin();

      return;
    }

    if (step === 3) {
      newPin = inputValue;

      inputValue = '';

      errorMessage = '';

      step = 4;

      focusPin();

      return;
    }

    if (step === 4) {
      confirmPin = inputValue;

      if (confirmPin !== newPin) {
        inputValue = '';

        triggerShake('PIN confirmation does not match');

        focusPin();

        return;
      }

      dispatch('save', {
        mode: hasExistingPin ? 'change' : 'create',
        currentPin,
        newPin,
        pinLength: selectedPinLength
      });

      reset();
    }
  }
</script>

{#if visible}
  <div class="fixed inset-0 z-[9999] bg-black/30 flex items-center justify-center">
    <div class="w-[420px] max-w-[95vw] bg-white rounded-xl border border-slate-300 shadow-xl">
      <!-- HEADER -->

      <div class="h-12 px-5 border-b border-slate-300 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <Lock size={16} />

          <span class="font-medium">{hasExistingPin ? 'Change PIN' : 'Create PIN'}</span>
        </div>

        <button on:click={close}>
          <X size={16} />
        </button>
      </div>

      <!-- BODY -->

      <div class="p-6">
        <div class="text-center mb-6">
          <div class="font-medium text-slate-800">
            {currentTitle}
          </div>

          {#if step !== 2}
            <div class="text-sm text-slate-500 mt-1">
              Enter
              {selectedPinLength}
              digit PIN
            </div>
          {/if}
        </div>

        <!-- PIN LENGTH -->

        {#if step === 2}
          <div class="flex justify-center gap-3">
            {#each pinLengths as len, i}
              <button
                bind:this={lengthButtons[i]}
                type="button"
                tabindex="0"
                class=" w-16 h-12 rounded-lg border font-medium transition {selectedPinLength === len
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-slate-300 hover:bg-slate-50'}"
                on:click={() => selectPinLength(len)}
                on:touchend|preventDefault={() => selectPinLength(len)}
                on:keydown={handleLengthKeydown}>
                {len}
              </button>
            {/each}
          </div>

          {#if hasExistingPin}
            <div class="mt-5 flex justify-center">
              <button
                type="button"
                class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
                disabled={deleting}
                on:click={deletePin}>
                <Trash2 size={16} />
                Delete PIN
              </button>
            </div>
          {/if}
        {:else}
          <div class="flex justify-center">
            <div class="relative" style="width: fit-content;">
              <input
                bind:this={pinInput}
                bind:value={inputValue}
                type="tel"
                inputmode="numeric"
                pattern="[0-9]*"
                maxlength={selectedPinLength}
                autocomplete="one-time-code"
                autocorrect="off"
                autocapitalize="off"
                spellcheck="false"
                class="
              absolute inset-0
              w-full h-full
              opacity-0
              z-20"
                style="caret-color: transparent;"
                disabled={verifying}
                on:input={handleInput} />

              <div class="flex gap-2" class:shake on:click={() => pinInput?.focus()}>
                {#each Array(selectedPinLength) as _, i}
                  <div
                    class="
                  w-11 h-11
                  rounded-md
                  border
                  flex items-center justify-center
                  text-lg font-semibold
                  transition-all

                  {i < inputValue.length ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-300 bg-white'}

                  {i === focusedIndex && inputValue.length < selectedPinLength
                      ? '!border-blue-500 ring-1 ring-blue-300'
                      : ''}
                  ">
                    {#if i < inputValue.length}
                      ●
                    {/if}
                  </div>
                {/each}
              </div>
            </div>
          </div>
        {/if}

        {#if errorMessage}
          <div class="mt-4 text-center text-sm text-red-600">
            {errorMessage}
          </div>
        {/if}
      </div>

      <!-- FOOTER -->

      <div class="px-5 py-4 border-t border-slate-300 bg-slate-50 flex justify-between items-center">
        <div class="text-xs text-slate-500">
          Step {displayStep} / {totalSteps}
        </div>

        <button class="px-4 py-2 rounded-lg border border-slate-300 hover:bg-white" on:click={close}>Cancel</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .shake {
    animation: shake-x 0.35s ease;
  }

  @keyframes shake-x {
    0% {
      transform: translateX(0);
    }
    15% {
      transform: translateX(-8px);
    }
    30% {
      transform: translateX(8px);
    }
    45% {
      transform: translateX(-6px);
    }
    60% {
      transform: translateX(6px);
    }
    75% {
      transform: translateX(-3px);
    }
    90% {
      transform: translateX(3px);
    }
    100% {
      transform: translateX(0);
    }
  }
</style>
